// src/lib/api.ts

// [수정 1] 배포 환경(AWS)과 개발 환경(Local) 구분
// REACT_APP_API_URL 환경 변수가 있으면 우선 사용
// 없으면 개발 환경에서는 localhost:3000 사용 (프록시 대신 직접 연결)
// 프로덕션 환경에서는 EC2 서버 주소 사용
const API_BASE_URL = process.env.REACT_APP_API_URL 
  || (process.env.NODE_ENV === 'development' 
    ? 'http://localhost:3000' 
    : 'http://43.201.208.249:3000'); 

export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
  count?: number
  pagination?: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
}

// 타입 정의 (그대로 유지)
export interface SensorData {
  id: number
  soil_moisture: number
  light_intensity: number
  temperature: number
  humidity: number
  timestamp: string
  device_id: string
  created_at: string
}

export interface Device {
  id: string
  name: string
  location: string
  status: string
  last_seen: string | null
  created_at: string
  updated_at: string
}

export interface ControlLog {
  id: number
  device_id: string
  action: string
  duration: number | null
  triggered_by: string
  sensor_data_id: number | null
  threshold_id: number | null
  created_at: string
}

export interface SensorThreshold {
  id: number
  device_id: string
  config_name: string
  temperature_threshold?: number
  humidity_threshold?: number
  soil_moisture_threshold?: number
  light_intensity_threshold?: number
  is_active: boolean
  created_at: string
  updated_at: string
  created_by?: string
}

class EasyFarmAPI {
  private baseURL: string

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL
  }

  // 공통 요청 함수
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      // 상대 경로('')일 때와 절대 경로('http...')일 때 모두 처리 가능하도록 구성
      const url = this.baseURL ? `${this.baseURL}${endpoint}` : endpoint
      
      // 디버깅: 개발 환경에서만 로그 출력
      if (process.env.NODE_ENV === 'development') {
        console.log(`[API Request] ${options.method || 'GET'} ${url}`)
      }
      
      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      })

      // 응답이 HTML인지 확인 (Content-Type 체크)
      const contentType = response.headers.get('content-type')
      
      // Content-Type이 JSON이 아니면 텍스트로 읽어서 확인
      if (contentType && !contentType.includes('application/json')) {
        const text = await response.text()
        // HTML 응답인 경우 (SPA 라우팅으로 인한)
        if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
          console.error(`API 요청이 HTML로 리다이렉트됨: ${url}`)
          return {
            success: false,
            error: `API 엔드포인트를 찾을 수 없습니다: ${endpoint}. 서버가 실행 중인지 확인해주세요.`
          }
        }
        // 다른 타입의 응답인 경우
        return {
          success: false,
          error: `예상치 못한 응답 형식: ${contentType}`
        }
      }

      // JSON 응답 파싱
      let data
      try {
        const text = await response.text()
        // 빈 응답인 경우 처리
        if (!text.trim()) {
          data = {}
        } else {
          data = JSON.parse(text)
        }
      } catch (parseError) {
        console.error(`JSON 파싱 오류 [${url}]:`, parseError)
        return {
          success: false,
          error: `서버 응답을 파싱할 수 없습니다: ${endpoint}`
        }
      }
      
      // 서버가 에러 메시지를 보낸 경우 처리
      // 서버에서 이미 { success: false, error: ... } 형식으로 반환하므로 그대로 반환
      if (!response.ok) {
        // 서버에서 반환한 데이터가 있으면 그대로 사용, 없으면 에러 생성
        if (data && typeof data === 'object') {
          return {
            success: false,
            error: data.error || `HTTP error! status: ${response.status}`
          }
        }
        throw new Error(data.error || `HTTP error! status: ${response.status}`)
      }

      return data
    } catch (error) {
      console.error(`API request failed [${endpoint}]:`, error)
      
      // JSON 파싱 오류인 경우
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        return {
          success: false,
          error: `서버 응답을 파싱할 수 없습니다. API 엔드포인트를 확인해주세요: ${endpoint}`
        }
      }
      
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      }
    }
  }

  // ==========================================
  // 1. 센서 데이터 API
  // ==========================================
  
  // 센서 데이터 저장 (아두이노에서 전송)
  async createSensorData(data: {
    soil_moisture: number
    light_intensity: number
    temperature: number
    humidity: number
    device_id?: string
  }): Promise<ApiResponse<{ success: boolean; command?: string | null }>> {
    return this.request<{ success: boolean; command?: string | null }>('/api/sensor-data', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // 센서 데이터 조회
  async getSensorData(params?: {
    limit?: number
    device_id?: string
    start_date?: string
    end_date?: string
    page?: number
    pageSize?: number
  }): Promise<ApiResponse<SensorData[]>> {
    const queryParams = new URLSearchParams()
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.device_id) queryParams.append('device_id', params.device_id)
    if (params?.start_date) queryParams.append('start_date', params.start_date)
    if (params?.end_date) queryParams.append('end_date', params.end_date)
    if (params?.page) queryParams.append('page', params.page.toString())
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString())

    const queryString = queryParams.toString()
    const endpoint = `/api/sensor-data${queryString ? `?${queryString}` : ''}`
    
    return this.request<SensorData[]>(endpoint)
  }

  // 최신 센서 데이터 조회 (단일 객체)
  async getLatestSensorData(deviceId: string): Promise<ApiResponse<SensorData>> {
    const response = await this.getSensorData({ device_id: deviceId, limit: 1 })
    
    if (response.success && response.data && response.data.length > 0) {
      return {
        success: true,
        data: response.data[0]
      }
    }
    
    return {
      success: false,
      error: '최신 센서 데이터를 찾을 수 없습니다.'
    }
  }

  // ==========================================
  // 2. 디바이스 API
  // ==========================================
  async getDevices(): Promise<ApiResponse<Device[]>> {
    return this.request<Device[]>('/api/devices')
  }

  // 디바이스 삭제
  async deleteDevice(deviceId: string): Promise<ApiResponse<{ message: string }>> {
    return this.request<{ message: string }>(`/api/devices/${deviceId}`, {
      method: 'DELETE'
    })
  }

  // 디바이스 생성
  async createDevice(data: { id: string; name: string; location?: string; status?: string }): Promise<ApiResponse<Device>> {
    return this.request<Device>('/api/devices', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // 디바이스 업데이트
  async updateDevice(deviceId: string, data: { name?: string; location?: string; status?: string }): Promise<ApiResponse<Device>> {
    return this.request<Device>(`/api/devices/${deviceId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  // 제어 로그 조회
  async getControlLogs(params?: { device_id?: string; limit?: number }): Promise<ApiResponse<ControlLog[]>> {
    const queryParams = new URLSearchParams()
    if (params?.device_id) queryParams.append('device_id', params.device_id)
    if (params?.limit) queryParams.append('limit', params.limit.toString())

    const queryString = queryParams.toString()
    const endpoint = `/api/control-logs${queryString ? `?${queryString}` : ''}`
    
    return this.request<ControlLog[]>(endpoint)
  }

  // 디바이스 상태 및 최근 로그 조회
  async getDeviceStatus(deviceId: string): Promise<ApiResponse<{
    sensor_data: SensorData | null
    control_logs: ControlLog[]
  }>> {
    return this.request(`/api/device-status/${deviceId}`)
  }

  // ==========================================
  // 3. 장치 제어 API (아두이노 명령 큐)
  // ==========================================
  async controlDevice(deviceId: string, action: string, duration?: number, triggered_by?: string): Promise<ApiResponse<{
    success: boolean
  }>> {
    return this.request<{ success: boolean }>(`/api/control/${deviceId}`, {
      method: 'POST',
      body: JSON.stringify({ action, duration, triggered_by: triggered_by || 'web_manual' })
    })
  }

  // ==========================================
  // 4. 임계치 설정 API (서버 주소와 일치시킴)
  // ==========================================
  
  // [수정] 주소 변경: /api/thresholds -> /api/threshold-configs
  async getThresholds(deviceId: string): Promise<ApiResponse<SensorThreshold[]>> {
    return this.request<SensorThreshold[]>(`/api/threshold-configs/${deviceId}`)
  }

  // 활성화된 설정 조회
  async getActiveThreshold(deviceId: string): Promise<ApiResponse<SensorThreshold>> {
    return this.request<SensorThreshold>(`/api/threshold-configs/${deviceId}/active`)
  }

  // 설정 생성
  async createThreshold(deviceId: string, data: any): Promise<ApiResponse<SensorThreshold>> {
    return this.request<SensorThreshold>(`/api/threshold-configs/${deviceId}`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // 설정 업데이트 (활성화/비활성화 등)
  async updateThreshold(id: number, data: any): Promise<ApiResponse<SensorThreshold>> {
    return this.request<SensorThreshold>(`/api/threshold-configs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  // 설정 삭제
  async deleteThreshold(id: number): Promise<ApiResponse<any>> {
    return this.request(`/api/threshold-configs/${id}`, {
      method: 'DELETE'
    })
  }
}

export const easyFarmAPI = new EasyFarmAPI()
export default easyFarmAPI